'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@pickleballinc/ui/components/ui/select';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LineupPlayerOption } from '@/entities';
import { AvailabilityStatus } from '@/entities/PlayerAvailability';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { isPlayerIneligible } from '@/utils/helpers';

interface LineupPlayerSelectProps {
	players: LineupPlayerOption[];
	onSelect: (selectedPlayerId: string | number) => void;
	selectedPlayer?: LineupPlayerOption;
	isLocked?: boolean;
	isBlurred?: boolean;
	playerLabel: string;
	placeholder: string;
	// Additional, contextual disable rule (e.g. tiebreaker doubles: already-picked / same-pair / gender).
	// OR'd with the internal availability check, so passing it never re-enables an unavailable player.
	isPlayerDisabledFn?: (player: LineupPlayerOption) => boolean;
	// When true, renders a "None" item that clears the slot. Selecting it emits '' to the parent.
	allowEmptySelection?: boolean;
}

const CLEAR_VALUE = '__clear__';

const STATUS_DOT_CLASS: Record<AvailabilityStatus, string> = {
	[AvailabilityStatus.AVAILABLE]: 'bg-green-500',
	[AvailabilityStatus.UNAVAILABLE]: 'bg-error-600',
	[AvailabilityStatus.MAYBE]: 'bg-gray-500',
	[AvailabilityStatus.UNDEFINED]: 'bg-green-500'
};

const StatusDot = ({ status }: { status?: AvailabilityStatus }) => (
	<span className={clsx('size-2 shrink-0 rounded-full', STATUS_DOT_CLASS[status ?? AvailabilityStatus.UNDEFINED])} />
);

export const LineupPlayerSelect = ({
	players,
	onSelect,
	selectedPlayer: initialSelectedPlayer,
	isLocked = false,
	isBlurred = false,
	playerLabel,
	placeholder,
	isPlayerDisabledFn,
	allowEmptySelection = false
}: LineupPlayerSelectProps) => {
	const teamLeagueInfo = useTeamLeagueInfo();

	const [selectedPlayer, setSelectedPlayer] = useState<LineupPlayerOption | undefined>(initialSelectedPlayer);

	// Keep the trigger in sync when the parent swaps the selected player (e.g. after a lineup change).
	useEffect(() => {
		setSelectedPlayer(initialSelectedPlayer);
	}, [initialSelectedPlayer?.userUuid]);

	// True once the player list has been scrolled away from the top. Drives the legend (shown at the
	// top) / scroll-down arrow (shown while scrolling) swap inside the dropdown.
	const [isScrolled, setIsScrolled] = useState(false);
	const detachScrollListener = useRef<(() => void) | null>(null);

	// Radix manages its own scroll viewport, so we hook into it via the content node to track scrolling.
	const handleContentRef = useCallback((content: HTMLDivElement | null) => {
		// Always detach the previous listener first so we never stack or leak one.
		detachScrollListener.current?.();
		detachScrollListener.current = null;

		// content === null means the dropdown closed — reset so the next open starts at the top.
		if (!content) {
			setIsScrolled(false);
			return;
		}

		// Grab Radix's scroll container (tagged with this data attribute).
		const viewport = content.querySelector<HTMLElement>('[data-radix-select-viewport]');
		if (!viewport) return;

		// Track whether the list has been scrolled away from the top; seed the initial value on open.
		const handleScroll = () => setIsScrolled(viewport.scrollTop > 0);
		handleScroll();
		viewport.addEventListener('scroll', handleScroll);
		// Remember how to detach this exact listener for the next open/close.
		detachScrollListener.current = () => viewport.removeEventListener('scroll', handleScroll);
	}, []);

	const sortedPlayers = useMemo(() => [...players].sort((a, b) => a.fullName.localeCompare(b.fullName)), [players]);

	const getNameLabel = (player: LineupPlayerOption) =>
		`${player.fullName}${teamLeagueInfo.useLineupStrength ? ` (${player.lineupStrength || 0})` : ''}`;

	const getRatingsDisplay = (player?: LineupPlayerOption) => {
		if (!player) return '';
		const ratings: string[] = [];
		if (teamLeagueInfo.showPlayerRatingsDoubles) ratings.push(`(D): ${player.formattedRatings.doubles}`);
		if (teamLeagueInfo.showPlayerRatingsSingles) ratings.push(`(S): ${player.formattedRatings.singles}`);
		if (teamLeagueInfo.showPlayerRatingsSkinnySingles) ratings.push(`(SS): ${player.formattedRatings.skinnySingles}`);
		return ratings.join(' ');
	};

	// A player is disabled when they're unavailable/ineligible or excluded by the caller's contextual rule.
	const isOptionDisabled = (player: LineupPlayerOption) => isPlayerIneligible(player) || (isPlayerDisabledFn?.(player) ?? false);

	const handleSelect = (value: string) => {
		// "None" item: clear the slot. Emit '' so callers that treat empty slots as '' round-trip cleanly.
		if (value === CLEAR_VALUE) {
			setSelectedPlayer(undefined);
			onSelect('');
			return;
		}
		const player = players.find((option) => option.userUuid === value);
		if (!player || isOptionDisabled(player)) return;
		setSelectedPlayer(player);
		onSelect(player.userUuid);
	};

	if (isLocked) {
		return (
			<div className="min-w-0 flex-1">
				<p className="mb-1.5 hidden text-sm font-medium text-gray-700 sm:block">{playerLabel}</p>
				{isBlurred ? (
					<div className="relative w-full">
						<div className="flex h-[66px] items-center rounded-lg border border-gray-300 bg-white px-3.5">
							<p className="text-sm text-gray-500">Player locked</p>
						</div>
						<div className="pointer-events-none absolute inset-0 rounded-lg border border-gray-300 bg-white/60 backdrop-blur-sm" />
					</div>
				) : (
					<p className="text-center text-base font-medium text-gray-900">{selectedPlayer?.fullName || 'TBD'}</p>
				)}
			</div>
		);
	}

	const triggerRatings = getRatingsDisplay(selectedPlayer);

	return (
		<div className="min-w-0 flex-1">
			<p className="mb-1.5 hidden text-sm font-medium text-gray-700 sm:block">{playerLabel}</p>

			<Select value={selectedPlayer?.userUuid || ''} onValueChange={handleSelect}>
				<SelectTrigger
					className="min-h-[42px] rounded-lg border-gray-300 bg-white px-3 py-0.5 text-start shadow-xs focus:ring-0"
					icon={({ className }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
				>
					{selectedPlayer ? (
						<span className="!flex min-w-0 flex-1 flex-col">
							<span className="truncate text-sm font-medium text-gray-900">{getNameLabel(selectedPlayer)}</span>
							{!!triggerRatings && <span className="truncate text-xs font-normal text-gray-700">{triggerRatings}</span>}
						</span>
					) : (
						<span className="truncate text-sm font-medium text-gray-500">{placeholder}</span>
					)}
				</SelectTrigger>

				{/* While unscrolled we show the legend and hide Radix's scroll-down arrow (the element right after the
				    viewport); once scrolling starts the legend hides and the arrow takes over. */}
				<SelectContent ref={handleContentRef} className={clsx('max-h-[300px]', !isScrolled && '[&_[data-radix-select-viewport]+*]:hidden')}>
					{allowEmptySelection ? (
						<SelectItem
							value={CLEAR_VALUE}
							className="cursor-pointer rounded-md focus:bg-darkorange-100 data-[state=checked]:bg-darkorange-100"
						>
							<span className="text-base font-medium leading-[18px] text-gray-900">None</span>
						</SelectItem>
					) : null}

					{sortedPlayers.length === 0 && !allowEmptySelection && (
						<div className="px-4 py-3 text-sm text-gray-500">No players available.</div>
					)}

					{sortedPlayers.map((player) => {
						const isDisabled = isOptionDisabled(player);
						const ratings = getRatingsDisplay(player);

						return (
							<SelectItem
								key={player.userUuid}
								value={player.userUuid}
								disabled={isDisabled}
								className="cursor-pointer rounded-md focus:bg-darkorange-100 data-[state=checked]:bg-darkorange-100"
							>
								<span className="flex min-w-0 items-center gap-2">
									<StatusDot status={player.isAvailableToPlay} />
									<span className="flex min-w-0 flex-col">
										<span
											className={clsx(
												'truncate text-base font-medium leading-[18px]',
												isDisabled ? 'text-gray-500' : 'text-gray-900'
											)}
										>
											{getNameLabel(player)}
										</span>
										{!!ratings && <span className="truncate text-xs leading-[18px] text-gray-500">{ratings}</span>}
									</span>
								</span>
							</SelectItem>
						);
					})}

					{/* Availability legend pinned to the bottom; hidden once the user starts scrolling the list. */}
					{!isScrolled && (
						<div className="sticky bottom-0 mt-1 border-t border-gray-200 bg-white px-4 pb-1 pt-2">
							<div className="flex items-center justify-between">
								<span className="flex items-center gap-2">
									<StatusDot status={AvailabilityStatus.AVAILABLE} />
									<span className="text-sm text-gray-600">Yes</span>
								</span>
								<span className="flex items-center gap-2">
									<StatusDot status={AvailabilityStatus.UNAVAILABLE} />
									<span className="text-sm text-gray-600">No</span>
								</span>
								<span className="flex items-center gap-2">
									<StatusDot status={AvailabilityStatus.MAYBE} />
									<span className="text-sm text-gray-600">Maybe</span>
								</span>
							</div>
						</div>
					)}
				</SelectContent>
			</Select>
		</div>
	);
};
